/* HealthspanX | Luminous Field motion, 16 October 2026.
   Component sequences, source-derived 36px/.985 entrances and restrained image depth.
   Server-rendered content stays visible. No commerce, article-body or price changes. */
(() => {
  'use strict';
  const root=document.documentElement;
  const reduced=matchMedia('(prefers-reduced-motion: reduce)');
  const desktop=matchMedia('(min-width: 1024px) and (hover: hover) and (pointer: fine)');
  const small=matchMedia('(max-width: 749px)');
  const still=()=>root.dataset.hsxMotion==='false'||reduced.matches||!!navigator.connection?.saveData;
  const supported='IntersectionObserver' in window;
  const records=new Map(), waiting=new Set(), active=new Set(), depth=new Map();
  const gallerySources=new WeakMap(), extras=new Set(), counted=new WeakSet();
  let depthFrame=0;
  const domain='#MainContent,.hsx-v2-footer';
  const exclude='[data-cart-page],[data-cart-drawer],.hsx-cart-line,form,dialog,[role="dialog"],[data-hsx-scene],.hsxp-mobile-buy,.hsx-prose,.hx-prose,.hsx-price,.hsx-v2-home-product__price,.hsx-product-facts,.hsxp-pack-picker,.hsx-gallery-nav,.hsx-toc';
  const groups=[
    '.hsxo-copy','.hsxo-heading',
    '.hsx-v2-hero__content','.hsx-heading','.hsx-article-header','.hsxp-journal-heading',
    '.hsx-product-info','.hsxv-copy','.hsxp-quality__copy','.hsxp-research__intro>div',
    '.hsxp-journal-feature>div','.hsxv-section-heading','.hsxv-paths-heading',
    '.hsxv-routine-heading','.hsxv-faq-layout>header','.hsxv-close-grid>div',
    '.hsxv-brand-statement','.hsxv-brand-grid>div','.hsxv-brand-end',
    '.hsxv-explore>header','.hsxj-help-close>div',
    '.hsx-v2-home-product__copy','.hsx-v2-home-science__copy',
    '.hsx-v2-home-journal__head>div','.hsx-v2-home-faq__grid>div:first-child',
    '.hsx-v2-home-close__grid>div:first-child',
    '.hx-evidence-v22 .hx-hero','.hx-study-v22 .hx-hero'
  ].join(',');
  const cards=[
    '.hsxo-benefit','.hsxo-steps>li','.hsxo-timeline>li','.hsxo-route-card',
    '.hsxv-path','.hsxv-study','.hsxp-study-preview','.hsx-research-card',
    '.hsx-product-card','.hsx-article-card','.hsx-search-result','.hsx-step',
    '.hsx-grid>.hsx-panel','.hsxv-explore-grid>a','.hsxj-reading-paths>a',
    '.hsx-v2-home-journal__card','.hsx-v2-home-quality__card',
    '.hsx-v2-home-evidence__card','.hsx-v2-home-tools__card',
    '.hsx-v2-home-product__detail','.hsxp-quality__rows>article',
    '.hsxv-quality-rows>article','.hsxv-numbered>li',
    '.hsx-faq>details','.hsx-v2-home-faq__item',
    '.hsx-v2-grid .hsx-v2-card','.hsx-v2-grid .hsx-v2-tile',
    '.hx-evidence-v22 .hx-card','.hx-evidence-v22 .hx-record','.hsxc-table tbody tr'
  ].join(',');
  const media=[
    '.hsxo-report-visual','.hsxo-program-visual','.hsxo-photo',
    '.hsxv-photo','.hsx-article-cover','.hsx-product-stage','.hsxv-label-card',
    '.hsxp-quality__visual','.hsxp-journal-feature__image',
    '.hsx-v2-home-product__visual','.hsxv-close-object'
  ].join(',');
  const depthSelector=[
    '.hsxv-split>.hsxv-photo','.hsxv-brand-hero>.hsxv-photo',
    '.hsxv-quality-grid>.hsxv-photo','.hsxv-research-top>.hsxv-photo',
    '.hsxv-research-hero>.hsxv-photo','.hsxv-support-hero>.hsxv-photo',
    '.hsxv-mosaic>.hsxv-photo','.hsxp-quality__visual'
  ].join(',');
  const standalone=[
    'h1','[data-hsx2-reveal]','.hsxp-vision h2','.hsxp-vision__bottom>p',
    '.hsxp-vision__bottom>.hsxb-action','.hsxv-mosaic-note',
    '.hsxj-faq-group>h2','.hsxj-help-topics','.hsxj-help-close>.hsx-v2-button',
    '.hsx-v2-home-heading','.hsx-v2-home-lead','.hsx-callout',
    '.hsx-v2-footer__title','.hsx-v2-footer__text','.hsx-v2-footer__nav',
    '.hsxj-support-strip>p','.hsxj-support-strip>nav','.hx-study-v22 .hx-snapshot',
    '.hx-evidence-v22 .hx-group-intro'
  ].join(',');
  const query=(scope,selector)=>{
    const nodes=[...scope.querySelectorAll(selector)];
    if(scope.matches?.(selector))nodes.unshift(scope);
    return nodes;
  };
  const allowed=el=>el.closest(domain)&&!el.closest(exclude)&&!el.querySelector('form,input,select,textarea')&&
    !el.matches('SCRIPT,STYLE,TEMPLATE,LINK');
  const state=(r,value)=>{r.state=value;r.el.dataset.hsxMotionReveal=value;};
  const classify=el=>el.matches('h1,h2,h3')?'heading':el.matches(media)?'image':
    el.matches('a,button,.hsx-actions,.hsx-v2-hero__links,.hsx-v2-home-close__links,.hsxj-article-actions')?'action':'copy';
  function settle(r,value='complete',done=true){
    observer?.unobserve(r.el);waiting.delete(r);active.delete(r);
    r.done=done;state(r,value);
    if(r.animation){const animation=r.animation;r.animation=null;animation.cancel();}
  }
  function enter(r){
    if(r.done||!r.el.isConnected)return;
    if(still()||!r.el.animate){settle(r,'static',false);return;}
    if(r.el.contains(document.activeElement)){settle(r);return;}
    const distance=r.kind==='row'?12:small.matches?22:36;
    const transform=r.kind==='image'||r.kind==='card'?
      `translate3d(0,${distance}px,0) scale(.985)`:
      `translate3d(0,${r.kind==='action'?distance*.65:distance}px,0)`;
    const duration=r.kind==='row'?650:r.kind==='image'?1000:r.kind==='heading'?900:800;
    const delay=Math.min(r.order,4)*85;
    state(r,'running');active.add(r);waiting.delete(r);observer.unobserve(r.el);
    const animation=r.el.animate([
      {opacity:r.kind==='image'?.72:.7,transform},
      {opacity:1,transform:'none'}
    ],{duration,delay,easing:'cubic-bezier(.22,.61,.36,1)',fill:'both'});
    animation.id='hsx-'+r.kind+'-entrance';r.animation=animation;
    animation.finished.then(()=>{
      if(r.animation!==animation)return;
      settle(r);queueDepth();
    },()=>{});
  }
  const observer=supported?new IntersectionObserver(entries=>{
    for(const entry of entries)if(entry.isIntersecting){const r=records.get(entry.target);if(r)enter(r);}
  },{threshold:.16,rootMargin:'0px 0px -10% 0px'}):null;

  function add(candidates,el,kind,order=0){
    if(allowed(el)&&!candidates.has(el))candidates.set(el,{el,kind,order,done:false,animation:null});
  }
  function init(scope=document){
    if(!scope.querySelectorAll)return;
    const candidates=new Map();
    query(scope,groups).forEach(group=>{
      const children=[...group.children].filter(allowed);
      children.forEach((el,index)=>{
        if(el.matches('.hsxp-quality__rows,.hsx-v2-home-product__details,.hsxv-numbered,.hsx-faq'))return;
        add(candidates,el,classify(el),index);
      });
    });
    query(scope,cards).forEach(el=>{
      const row=[...el.parentElement.children].filter(e=>e.matches(cards));
      add(candidates,el,el.matches('tr')?'row':'card',Math.max(0,row.indexOf(el))%4);
    });
    query(scope,media).forEach(el=>add(candidates,el,'image'));
    query(scope,standalone).forEach(el=>add(candidates,el,classify(el)));
    for(const r of candidates.values()){
      let ancestor=r.el.parentElement,nested=false;
      while(ancestor&&!ancestor.matches(domain)){
        if(candidates.has(ancestor)||records.has(ancestor)){nested=true;break;}
        ancestor=ancestor.parentElement;
      }
      if(nested||records.has(r.el))continue;
      records.set(r.el,r);r.el.dataset.hsxMotionKind=r.kind;
      r.el.dataset.hsxMotionOrder=String(r.order);
      const rect=r.el.getBoundingClientRect();
      const critical=rect.top<innerHeight&&rect.bottom>0&&(r.el.matches('h1,img[fetchpriority="high"]')||r.el.querySelector('h1,img[fetchpriority="high"]'));
      if(critical){r.done=true;state(r,'static');continue;}
      if(still()||!supported){state(r,'static');continue;}
      state(r,'ready');waiting.add(r);observer.observe(r.el);
    }
    query(scope,depthSelector).forEach(frame=>{
      if(depth.has(frame)||frame.closest('a'))return;
      const image=frame.querySelector('img');if(!image)return;
      const item={frame,image,visible:false};depth.set(frame,item);depthObserver?.observe(frame);
    });
    query(scope,'[data-product-image]').forEach(img=>{
      if(!gallerySources.has(img))gallerySources.set(img,img.currentSrc||img.src);
    });
    if(!still())query(scope,'[data-hsx-count-to]').forEach(el=>{if(!counted.has(el))countObserver?.observe(el);});
    syncDepth();
  }

  const canDepth=()=>supported&&!still()&&desktop.matches&&!document.hidden;
  function clearDepth(item){
    delete item.image.dataset.hsxDepthImage;item.image.style.removeProperty('--hsx-depth-y');
    item.frame.dataset.hsxMotionDepth='static';
  }
  function updateDepth(){
    depthFrame=0;
    for(const item of depth.values()){
      if(!item.frame.isConnected||!item.visible||!canDepth()){clearDepth(item);continue;}
      item.image.dataset.hsxDepthImage='true';item.frame.dataset.hsxMotionDepth='active';
      const r=records.get(item.frame);
      if(r&&!r.done){item.image.style.setProperty('--hsx-depth-y','0px');continue;}
      const rect=item.frame.getBoundingClientRect();
      const progress=(innerHeight/2-(rect.top+rect.height/2))/(innerHeight+rect.height);
      const shift=Math.max(-8,Math.min(8,progress*24));
      item.image.style.setProperty('--hsx-depth-y',shift.toFixed(2)+'px');
    }
  }
  function queueDepth(){if(!depthFrame&&canDepth())depthFrame=requestAnimationFrame(updateDepth);}
  function syncDepth(){
    if(canDepth())queueDepth();
    else{
      if(depthFrame)cancelAnimationFrame(depthFrame);depthFrame=0;
      depth.forEach(clearDepth);
    }
  }
  const depthObserver=supported?new IntersectionObserver(entries=>{
    entries.forEach(e=>{const item=depth.get(e.target);if(item){item.visible=e.isIntersecting;if(!item.visible)clearDepth(item);}});
    queueDepth();
  },{rootMargin:'8% 0px 8% 0px'}):null;

  const countObserver=supported?new IntersectionObserver(entries=>{
    entries.forEach(entry=>{
      if(!entry.isIntersecting)return;
      const el=entry.target;countObserver.unobserve(el);
      if(counted.has(el))return;counted.add(el);
      const goal=Number(el.dataset.hsxCountTo);
      if(!Number.isSafeInteger(goal)||goal<0||goal>1000||still())return;
      const begin=performance.now();
      const frame=now=>{
        if(!el.isConnected||still()){el.textContent=String(goal);return;}
        const p=Math.min(1,Math.max(0,(now-begin)/1100));
        el.textContent=String(Math.round(goal*(1-Math.pow(1-p,3))));
        if(p<1)requestAnimationFrame(frame);
      };requestAnimationFrame(frame);
    });
  },{threshold:.55}):null;

  function preference(){
    for(const r of records.values()){
      if(still()){const done=r.done||r.state==='running';settle(r,'static',done);}
      else if(!r.done&&supported){state(r,'ready');waiting.add(r);observer.observe(r.el);}
    }
    if(still()){for(const animation of extras)animation.cancel();extras.clear();}
    syncDepth();
  }
  function interaction(event){
    const gallery=event.target.closest?.('.hsx-product-gallery');
    for(const r of [...waiting,...active]){
      if(r.el.contains(event.target)||gallery?.contains(r.el))settle(r);
    }
  }
  document.addEventListener('focusin',interaction);
  document.addEventListener('pointerdown',interaction,{passive:true});
  document.addEventListener('load',event=>{
    const img=event.target;if(!img.matches?.('[data-product-image]'))return;
    const src=img.currentSrc||img.src,previous=gallerySources.get(img);gallerySources.set(img,src);
    if(!previous||src===previous||still()||!img.animate)return;
    const animation=img.animate([{opacity:.55},{opacity:1}],{duration:320,easing:'ease-out'});
    animation.id='hsx-gallery-image';extras.add(animation);
    animation.finished.catch(()=>{}).finally(()=>extras.delete(animation));
  },true);
  window.addEventListener('scroll',queueDepth,{passive:true});
  window.addEventListener('resize',syncDepth,{passive:true});
  reduced.addEventListener?.('change',preference);
  desktop.addEventListener?.('change',syncDepth);
  navigator.connection?.addEventListener?.('change',preference);
  new MutationObserver(preference).observe(root,{attributes:true,attributeFilter:['data-hsx-motion']});
  document.addEventListener('visibilitychange',()=>{
    if(document.hidden)for(const r of [...active])settle(r);
    syncDepth();
  });
  document.addEventListener('shopify:section:load',e=>init(e.target));
  document.addEventListener('shopify:section:reorder',()=>init(document));
  document.addEventListener('shopify:section:unload',event=>{
    for(const [el,r] of records)if(event.target.contains(el)){settle(r);records.delete(el);}
    for(const [el,item] of depth)if(event.target.contains(el)){clearDepth(item);depthObserver?.unobserve(el);depth.delete(el);}
  });
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',()=>init(document),{once:true});
  else init(document);
})();
